import Vue from 'vue'
import Router from 'vue-router'
import auth from './auth'
import user from './user'
import home from './home'
import file from './file'
// import request from './request'
import templates from './templates'
import providers from './providers'
import requests from './requests'
import tradies from './tradies'
import candidates from './candidates'
import jobs from './jobs'
import openToWork from './openToWork'
import projects from './projects'
import businessAdmins from './businessAdmins'
import timesheets from './timesheets'
import schedule from './schedule'
// import subscription from './subscription'
import trades from './trades'

import store from '../store'
import {
  canEnterBusinessAdminRoute,
  firstAccessibleLanding,
} from '@/utils/routePermissions'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    auth,
    user,
    home,
    file,
    // request,
    templates,
    providers,
    requests,
    tradies,
    candidates,
    jobs,
    openToWork,
    projects,
    businessAdmins,
    timesheets,
    schedule,
    // subscription,
    trades,
    {
      path: '/',
      redirect: { name: 'auth.login-page' },
    },
    {
      path: '/service-unavailable',
      name: 'service-unavailable',
      component: () =>
        import(
          /* webpackChunkName: "service-unavailable" */ '@/views/error/ServiceUnavailable'
        ),
    },
    {
      path: '*',
      name: 'page-not-found',
      component: () =>
        import(
          /* webpackChunkName: "page-not-found" */ '@/views/error/PageNotFound'
        ),
    },
    {
      path: '/premium-settings',
      name: 'premium-settings',
      component: () =>
        import(
          /* webpackChunkName: "premium-settings" */ '@/views/error/PremiumSettings'
        ),
    },
  ],
  //  this feature only works if the browser supports history.pushState
  scrollBehavior(to, from, savedPosition) {
    return { x: 0, y: 0 }
  },

  watch: {
    $route() {
      this.$store.dispatch('notification/getUnreadNotifications')
    },
  },
})

// Throttle permission refreshes in the nav guard to at most once per 30 s.
// The guard must still run on every navigation for redirect logic, but there
// is no need to hit /auth/me on every tab change — MainLayout.created() covers
// the periodic refresh when the user is already authenticated.
let _navMeLastFetch = 0

router.beforeEach(async (to, from, next) => {
  try {
    const authRouteNames = [
      'auth.login-page',
      'auth.forgot-password-page',
      'auth.reset-password-page',
      'auth.change-password-page',
      'auth.mfa-page',
      'auth.register-page',
      'auth.account-type-selection-page',
      'auth.onboarding-company',
      'auth.onboarding-tradie',
      'service-unavailable',
    ]

    // If there is no access token yet and we're heading to an auth page,
    // don't spam /auth/me – just allow navigation.
    if (
      !store.state.auth.token.access_token &&
      authRouteNames.includes(to.name)
    )
      return next()

    if (!store.getters['auth/user'] || !store.state.auth.token.access_token) {
      try {
        await store.dispatch('auth/getAuthenticatedUser')
        _navMeLastFetch = Date.now()
      } catch (err) {
        // Session hydration failed (network hiccup, 5xx, expired token, etc).
        // Don't fall through to the outer catch's bare next() below — that
        // would skip the MFA-required redirect and every permission gate in
        // this guard and let the request straight into a protected route.
        if (to.name === 'auth.login-page') return next()
        return next({ name: 'auth.login-page' })
      }
    } else if (Date.now() - _navMeLastFetch > 30000) {
      try {
        await store.dispatch('auth/getAuthenticatedUser')
        _navMeLastFetch = Date.now()
      } catch (err) {}
    }

    const user = store.getters['auth/user']
    const isMfaRequired = store.getters['auth/isMfaRequired']
    const isMfaVerified = store.state.auth.isMfaVerified

    // Check if MFA is required but not verified
    if (
      user &&
      isMfaRequired &&
      !isMfaVerified &&
      to.name !== 'auth.mfa-page'
    ) {
      next({ name: 'auth.mfa-page' })
      return
    }

    // First-login BA must set a new password — but keep a way out. Without the
    // login route whitelisted here, a hard refresh (token rehydrated from
    // storage, so the no-token auth bypass above doesn't apply) traps them on
    // /change-password: every other route, /login included, redirects straight
    // back. The sibling onboarding blocks below already allow auth.login-page.
    if (
      user.user_type === 'business_admin' &&
      user.first_login &&
      !['auth.change-password-page', 'auth.login-page'].includes(to.name)
    )
      return next({ name: 'auth.change-password-page' })

    // Tradie "onboarding" is the web form (avatar required). CSV import fills
    // full_name but not a profile photo — use avatar presence, not full_name.
    const onboarded =
      user.user_type === 'tradie'
        ? !!(
            user.avatar &&
            typeof user.avatar === 'object' &&
            (user.avatar.id != null || user.avatar.url)
          )
        : user.full_name !== ''
    if (
      user.user_type === 'tradie' &&
      !onboarded &&
      ![
        'auth.onboarding-tradie',
        'auth.change-password-page',
        'auth.login-page',
        'auth.account-type-selection-page',
      ].includes(to.name)
    ) {
      // console.log('Not onboarded, redirecting...')
      return next({
        name: 'auth.onboarding-tradie',
        params: { previousForm: { email: user.email, emailOrPhone: 'email' } },
      })
    }

    const baOnBoarded = user.full_name !== ''

    if (
      user.user_type === 'business_admin' &&
      !baOnBoarded &&
      ![
        'auth.onboarding-company',
        'auth.change-password-page',
        'auth.login-page',
        'auth.account-type-selection-page',
      ].includes(to.name)
    ) {
      return next({ name: 'auth.onboarding-company' })
    }

    // Project-share BAs whose 30-day grace has expired must complete the full
    // company profile before they can access the rest of the app.
    if (
      user.user_type === 'business_admin' &&
      user.profile_completion_required &&
      ![
        'auth.onboarding-company',
        'auth.change-password-page',
        'auth.login-page',
      ].includes(to.name)
    ) {
      return next({ name: 'auth.onboarding-company' })
    }

    // Business Admins without a token: lock them to My Account -> Profile,
    // except for the primary BA (first created) who can use the full app.
    // Skip this whole block on auth routes to avoid extra traffic / loops.
    if (!authRouteNames.includes(to.name)) {
      const isBusinessAdmin = user.user_type === 'business_admin'
      const hasToken = !!user.has_token
      const isPrimaryBusinessAdmin = !!user.is_primary

      // Project-scoped business admins (supervisors and any project-scoped role)
      // are exempt from the token gate — their access is governed by role +
      // assigned projects, not by holding a token seat. Prefer the explicit
      // `is_project_scoped` flag; fall back to the legacy supervisor role string.
      const isProjectScopedRole =
        user.is_project_scoped === true ||
        (user.is_project_scoped == null && user.role === 'supervisor')
      const isFreeTier = !!user.is_free
      const isBusinessAdminWithoutToken =
        isBusinessAdmin &&
        !hasToken &&
        !isPrimaryBusinessAdmin &&
        !isProjectScopedRole &&
        !isFreeTier
      if (
        isBusinessAdminWithoutToken &&
        ![
          'user.settings-page',
          'user.verify-page',
          'user.verify-only-page',
        ].includes(to.name)
      ) {
        const unverified = !user.email_verified || !user.phone_number_verified
        return next({
          name: 'user.settings-page',
          // Let newly-invited BAs still verify email/phone on first login.
          hash: unverified ? '#account' : '#profile',
          query: { ba_no_token: '1' },
        })
      }
    }

    // Project-scoped business admins are restricted: they may only reach the
    // Projects area plus their own account/profile (and the verification flows).
    // Anything else bounces to the projects list. A business admin on a
    // company-wide role (is_project_scoped false) is NOT restricted here — their
    // tab access is governed by permissions. Backend still enforces per-project
    // access (see ROLES_PERMISSIONS_BACKEND.md §9).
    const isProjectScopedUser =
      user.user_type === 'business_admin' &&
      (user.is_project_scoped === true ||
        (user.is_project_scoped == null && user.role === 'supervisor'))

    // Business admins on a role with NO permissions can't open any feature tab.
    // Rather than strand them on an empty screen, lock them to their own account
    // (Profile/Account) just like a tokenless BA. `permissions` is an explicit
    // empty array here — a missing array (legacy payload) fails open, and `["*"]`
    // or any key means they have access, so neither is trapped. Primary admins
    // and project-scoped users are handled above and excluded.
    const resolvedPermissions = Array.isArray(user.permissions)
      ? user.permissions
      : null
    const businessAdminHasNoPermissions =
      user.user_type === 'business_admin' &&
      !user.is_primary &&
      !isProjectScopedUser &&
      resolvedPermissions !== null &&
      resolvedPermissions.length === 0
    if (
      businessAdminHasNoPermissions &&
      ![
        'user.settings-page',
        'user.verify-page',
        'user.verify-only-page',
      ].includes(to.name)
    ) {
      const unverifiedNoAccess =
        !user.email_verified || !user.phone_number_verified
      return next({
        name: 'user.settings-page',
        hash: unverifiedNoAccess ? '#account' : '#profile',
        query: { no_access: '1' },
      })
    }

    // Supervisor tradies: can access the same routes as regular tradies plus projects.
    if (
      user.user_type === 'tradie' &&
      user.is_supervisor &&
      !authRouteNames.includes(to.name)
    ) {
      const allowedForSupervisor =
        to.name &&
        (to.name.startsWith('projects.') ||
          to.name.startsWith('home.') ||
          to.name.startsWith('file.') ||
          to.name.startsWith('request.') ||
          to.name.startsWith('requests.') ||
          to.name.startsWith('timesheets.') ||
          to.name.startsWith('schedule.') ||
          to.name.startsWith('templates.') ||
          to.name.startsWith('template.') ||
          to.name.startsWith('open-to-work') ||
          to.name.startsWith('user.'))
      if (!allowedForSupervisor) {
        return next({ name: 'home.welcome-page' })
      }
    }

    // Permission-gated areas: a business admin must hold the matching view
    // permission to enter (hides direct-URL / deep-link access to a tab that's
    // missing from their nav). Full-access users and tradies pass via the
    // store's hasPermission (wildcard / fail-open). Project-scoped users are
    // already constrained above. A BA missing the permission is sent to their
    // first accessible area instead of the blocked page.
    // Project-scoped users are fully restricted by the block above — skip the
    // per-permission gate so they don't get bounced back and forth between
    // projects.list-page (the only place they're allowed) and whatever
    // firstAccessibleLanding() returns for their other permissions.
    if (
      user.user_type === 'business_admin' &&
      !authRouteNames.includes(to.name)
    ) {
      const can = store.getters['auth/hasPermission']
      if (!canEnterBusinessAdminRoute(can, to.name)) {
        return next(firstAccessibleLanding(can))
      }
    }

    const unverified = !user.email_verified || !user.phone_number_verified
    if (
      onboarded &&
      unverified &&
      // to.name + to.hash !== 'user.settings-page#account' &&
      to.name !== 'user.settings-page' &&
      to.name !== 'user.verify-page' &&
      to.name !== 'user.verify-only-page' &&
      to.name !== 'auth.change-password-page' &&
      to.name !== 'auth.mfa-page'
    )
      return next({ name: 'user.settings-page', hash: '#account' })

    next()
  } catch (err) {
    console.log(err)
    next()
  }
})

export default router
